/**
 * 护理方案状态管理
 * Requirements: 11.1, 11.5, 11.6
 */

import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type {
  CareTimeline,
  CareLog,
  CareLogsMeta,
  EscalationAlert,
} from "@/types/care-plan";

interface CarePlanState {
  // 护理时间轴数据
  timeline: CareTimeline | null;

  // 护理日志
  careLogs: CareLog[];
  careLogsMeta: CareLogsMeta | null;

  // 已完成的任务 ID 集合
  completedTaskIds: Set<string>;

  // 待删除的任务 ID 集合（乐观更新时标记，防止 query 刷新覆盖）
  pendingRemovalTaskIds: Set<string>;

  // 升级警报
  escalationAlert: EscalationAlert | null;

  // 加载状态
  isLoading: boolean;
  isUnlocking: boolean;
  isStarting: boolean;
  isCompleting: boolean;

  // 错误状态
  error: string | null;

  // Actions
  setTimeline: (timeline: CareTimeline | null) => void;
  setCareLogs: (logs: CareLog[], meta: CareLogsMeta) => void;
  addCareLog: (log: CareLog) => void;
  updateCareLog: (logId: number, updates: Partial<CareLog>) => void;
  removeCareLog: (logId: number) => void;

  markTaskCompleted: (taskId: string) => void;
  markTaskIncomplete: (taskId: string) => void;
  clearPendingRemoval: (taskId: string) => void;

  setEscalationAlert: (alert: EscalationAlert | null) => void;

  setLoading: (loading: boolean) => void;
  setUnlocking: (unlocking: boolean) => void;
  setStarting: (starting: boolean) => void;
  setCompleting: (completing: boolean) => void;
  setError: (error: string | null) => void;

  // 计算属性
  isUnlocked: () => boolean;
  isCareStarted: () => boolean;
  isCareCompleted: () => boolean;
  currentDay: () => number;

  // 重置状态
  reset: () => void;
}

const initialState = {
  timeline: null,
  careLogs: [],
  careLogsMeta: null,
  completedTaskIds: new Set<string>(),
  pendingRemovalTaskIds: new Set<string>(),
  escalationAlert: null,
  isLoading: false,
  isUnlocking: false,
  isStarting: false,
  isCompleting: false,
  error: null,
};

export const useCarePlanStore = create<CarePlanState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // 设置护理时间轴
      setTimeline: (timeline) => set({ timeline, error: null }),

      // 设置护理日志
      setCareLogs: (logs, meta) =>
        set((state) => {
          // 从日志中提取已完成的任务 ID
          const completedFromLogs = new Set(
            logs
              .filter(
                (log) => log.log_type === "task_completed" && log.task_data,
              )
              .map((log) => (log.task_data as { task_id?: string })?.task_id)
              .filter((id): id is string => !!id),
          );

          // 合并：保留乐观更新的状态 + 服务端返回的状态
          // 但排除正在待删除的任务（pendingRemovalTaskIds）
          const mergedCompleted = new Set<string>();

          // 添加服务端返回的已完成任务
          completedFromLogs.forEach((id) => {
            if (!state.pendingRemovalTaskIds.has(id)) {
              mergedCompleted.add(id);
            }
          });

          // 添加本地乐观更新的任务（如果没有被标记为待删除）
          state.completedTaskIds.forEach((id) => {
            if (!state.pendingRemovalTaskIds.has(id)) {
              mergedCompleted.add(id);
            }
          });

          return {
            careLogs: logs,
            careLogsMeta: meta,
            completedTaskIds: mergedCompleted,
          };
        }),

      // 添加护理日志
      addCareLog: (log) =>
        set((state) => ({
          careLogs: [log, ...state.careLogs],
        })),

      // 更新护理日志
      updateCareLog: (logId, updates) =>
        set((state) => ({
          careLogs: state.careLogs.map((log) =>
            log.id === logId ? { ...log, ...updates } : log,
          ),
        })),

      // 删除护理日志
      removeCareLog: (logId) =>
        set((state) => ({
          careLogs: state.careLogs.filter((log) => log.id !== logId),
        })),

      // 标记任务完成
      markTaskCompleted: (taskId) =>
        set((state) => {
          // 从待删除列表中移除（如果存在）
          const newPending = new Set(state.pendingRemovalTaskIds);
          newPending.delete(taskId);

          return {
            completedTaskIds: new Set([...state.completedTaskIds, taskId]),
            pendingRemovalTaskIds: newPending,
          };
        }),

      // 标记任务未完成
      markTaskIncomplete: (taskId) =>
        set((state) => {
          const newCompleted = new Set(state.completedTaskIds);
          newCompleted.delete(taskId);

          // 添加到待删除列表，防止 query 刷新时恢复
          const newPending = new Set(state.pendingRemovalTaskIds);
          newPending.add(taskId);

          return {
            completedTaskIds: newCompleted,
            pendingRemovalTaskIds: newPending,
          };
        }),

      // 清除待删除标记（API 删除成功后调用）
      clearPendingRemoval: (taskId) =>
        set((state) => {
          const newPending = new Set(state.pendingRemovalTaskIds);
          newPending.delete(taskId);
          return { pendingRemovalTaskIds: newPending };
        }),

      // 设置升级警报
      setEscalationAlert: (alert) => set({ escalationAlert: alert }),

      // 加载状态
      setLoading: (loading) => set({ isLoading: loading }),
      setUnlocking: (unlocking) => set({ isUnlocking: unlocking }),
      setStarting: (starting) => set({ isStarting: starting }),
      setCompleting: (completing) => set({ isCompleting: completing }),
      setError: (error) => set({ error }),

      // 计算属性
      isUnlocked: () => get().timeline?.unlock_status.is_unlocked ?? false,
      isCareStarted: () => get().timeline?.unlock_status.care_started ?? false,
      isCareCompleted: () => get().careLogsMeta?.care_completed ?? false,
      currentDay: () => get().timeline?.unlock_status.current_day ?? 0,

      // 重置状态
      reset: () => set(initialState),
    }),
    { name: "care-plan-store" },
  ),
);

export default useCarePlanStore;
